import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ReviewItem } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { ErrorState, LineDot, Loading, Segmented, useTitle } from '../../ui/bits';
import { Fig, PageHead } from '../../ui/viz';
import { StDot } from '../../catalog/kit';
import { peso } from '../../lib/format.js';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { STATE_ART } from '../../lib/voice.js';
import { ProductArt } from '../../catalog/art';
import { opsKeys, useOpsSettings } from '../queries';
import { DialogActions, Modal, StaleNote, TextField } from '../payments/kit';
import { manilaStamp } from '../reports/period.js';
import { REJECT_NOTE, RULES_SPEC, decide, reviewKey, describeRule, putRules, reviewReason, rulesDirty, rulesDraft, sortQueue, validateRules, type RuleKey } from './lib.js';
import '../../catalog/catalog.css';

type Server = Record<string, Record<string, unknown>>;
type Drafts = Record<RuleKey, Record<string, string | boolean>>;
type QueueStatus = 'pending' | 'rejected';

const KEYS: RuleKey[] = ['catalog', 'catchWeight'];
const LINE_WORD: Record<string, string> = { fresh: 'Fresh', grill: 'Grill', mart: 'Grocery' };

/** /ops/catalogue: the `catalog` and `catchWeight` rules, and the partner product review queue. */
export function OpsCataloguePage() {
  useTitle('Catalogue rules');
  const [status, setStatus] = useState<QueueStatus>('pending');
  const pending = useQuery({ queryKey: reviewKey('pending'), queryFn: () => api.opsReviewQueue('pending'), refetchInterval: 30_000 });
  const waiting = pending.data?.length ?? null;

  return (
    <>
      <PageHead title="Catalogue rules" meta={waiting === null ? 'Partner products and catch-weight' : `${waiting} waiting for review`} live={waiting !== null} />

      <section className="card dv-card dv-rise" aria-labelledby="cr-queue-h" style={{ ['--i' as string]: 0 }}>
        <div className="dv-card-h">
          <h2 id="cr-queue-h" title="Customers never see a product while it waits or after it's not approved. The shop sees your note on its product.">
            Review queue
          </h2>
          {waiting ? <span className="dv-chip bad">{waiting}</span> : null}
          <span className="grow" />
          <Segmented<QueueStatus>
            label="Show"
            value={status}
            options={[
              { value: 'pending', label: waiting ? `Waiting · ${waiting}` : 'Waiting' },
              { value: 'rejected', label: 'Not approved' },
            ]}
            onChange={setStatus}
          />
        </div>
        <ReviewQueue status={status} pendingQuery={pending} />
      </section>

      <RulesForm />
    </>
  );
}

function ReviewQueue({ status, pendingQuery }: { status: QueueStatus; pendingQuery: UseQueryResult<ReviewItem[]> }) {
  const qc = useQueryClient();
  const toast = useToast();
  const rejectedQuery = useQuery({ queryKey: reviewKey('rejected'), queryFn: () => api.opsReviewQueue('rejected'), enabled: status === 'rejected' });
  const q = status === 'pending' ? pendingQuery : rejectedQuery;
  const [rejecting, setRejecting] = useState<ReviewItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const decision = useMutation({
    mutationFn: (v: { item: ReviewItem; decision: 'approve' | 'reject'; note?: string }) => decide(api, v.item.productId, v.decision, v.note),
    onMutate: (v) => setBusyId(v.item.productId),
    onSuccess: (r, v) => {
      if (!r.ok) return;
      toast(v.decision === 'approve' ? `${v.item.name} is live for customers.` : `${v.item.name} stays hidden. ${v.item.shopName} sees your note.`);
      setRejecting(null);
      void qc.invalidateQueries({ queryKey: ['ops', 'review'] });
    },
    onError: (err) => toast(errorMessage(err, "Couldn't save the decision. Try again."), 'error'),
    onSettled: () => setBusyId(null),
  });

  const items = q.data ? sortQueue(q.data) : [];
  return (
    <>
      {q.isError && !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Loading label="Loading the review queue" rows={2} />
      ) : items.length === 0 ? (
        status === 'pending' ? (
          <div className="dv-quiet" title="New partner products and big price jumps land here when review is on.">
            <Icon name="check" />
            Nothing to review
          </div>
        ) : (
          <div className="dv-quiet" title="Products you don't approve show up here with your note.">
            <Icon name="check" />
            No products turned down
          </div>
        )
      ) : (
        <>
          <StaleNote show={q.isError} what="the queue" />
          <div className="table-wrap">
            <table className="mk-table">
              <thead>
                <tr>
                  <th scope="col">Product</th>
                  <th scope="col">Why</th>
                  <th scope="col" className="num">
                    Price
                  </th>
                  <th scope="col">{status === 'pending' ? 'Waiting since' : 'Your note'}</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const busy = busyId === it.productId;
                  return (
                    <tr key={it.productId}>
                      <td className="ops-wrap">
                        <span className="mk-who-in">
                          <ProductArt className="cat-thumb" src={it.imageUrl} name={it.name} line={it.line} />
                          <span className="stack s4" style={{ minWidth: 0 }}>
                            <span className="mk-name">{it.name}</span>
                            <span className="mk-st">
                              <LineDot line={it.line} />
                              <span>{LINE_WORD[it.line] ?? it.line}</span>
                              <span className="mk-sub">· {it.shopName}</span>
                            </span>
                          </span>
                        </span>
                      </td>
                      <td>
                        <StDot tone={it.reason === 'price_jump' ? 'warn' : 'info'}>{reviewReason(it)}</StDot>
                      </td>
                      <td className="num">
                        <span className="mk-numtxt">{it.priceLabel}</span>
                      </td>
                      <td className={status === 'pending' ? undefined : 'ops-wrap'}>
                        {status === 'pending' ? <span className="mk-sub">{manilaStamp(it.updatedAt)}</span> : <span className="mk-sub">{it.reviewNote ?? '—'}</span>}
                      </td>
                      <td className="num">
                        <div className="actions">
                          {status === 'pending' ? (
                            <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setRejecting(it)} aria-label={`Don't approve ${it.name}`}>
                              Don't approve
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={busy}
                            aria-busy={(busy && decision.variables?.decision === 'approve') || undefined}
                            onClick={() => decision.mutate({ item: it, decision: 'approve' })}
                            aria-label={`Approve ${it.name}`}
                          >
                            <Icon name="check" />
                            Approve
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <RejectDialog
        item={rejecting}
        busy={decision.isPending}
        onClose={() => setRejecting(null)}
        onReject={async (note) => {
          if (!rejecting) return null;
          const r = await decision.mutateAsync({ item: rejecting, decision: 'reject', note }).catch(() => null);
          return r && !r.ok ? r.error : null;
        }}
      />
    </>
  );
}

function RejectDialog({ item, busy, onClose, onReject }: { item: ReviewItem | null; busy: boolean; onClose(): void; onReject(note: string): Promise<string | null> }) {
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | undefined>();
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  useEffect(() => {
    setNote('');
    setErr(undefined);
  }, [item?.productId]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = await onReject(note);
    if (problem) {
      setErr(problem);
      ref.current?.focus();
    }
  };
  return (
    <Modal open={item !== null} labelledBy="cr-reject-title" onClose={onClose}>
      <form className="stack s24" onSubmit={(e) => void submit(e)} noValidate>
        <div className="stack s4">
          <h2 id="cr-reject-title" className="t-headline">
            Don't approve {item?.name}?
          </h2>
          <span className="t-caption muted">It stays hidden from customers. {item?.shopName} sees your note on the product and can fix it.</span>
        </div>
        <TextField id="cr-reject-note" label="Note for the shop" value={note} onChange={setNote} error={err} multiline maxLength={REJECT_NOTE.max} help="Say what to change, like “Add a real photo” or “The price looks off”." inputRef={ref} />
        <DialogActions busy={busy} busyLabel="Saving…" confirm="Don't approve" danger onCancel={onClose} />
      </form>
    </Modal>
  );
}

function RulesForm() {
  const settings = useOpsSettings();
  const qc = useQueryClient();
  const toast = useToast();
  const server = settings.data as unknown as Server | undefined;
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [errors, setErrors] = useState<Partial<Record<RuleKey, Record<string, string>>>>({});
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (server && !drafts) setDrafts({ catalog: rulesDraft('catalog', server.catalog), catchWeight: rulesDraft('catchWeight', server.catchWeight) });
  }, [server, drafts]);

  if (settings.isError && !server)
    return (
      <section className="card">
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      </section>
    );
  if (!server || !drafts) return <Loading label="Loading the rules" rows={2} />;

  const dirty = KEYS.filter((k) => rulesDirty(k, drafts[k], server[k]));

  const setField = (key: RuleKey, name: string, v: string | boolean) => {
    setDrafts((d) => (d ? { ...d, [key]: { ...d[key], [name]: v } } : d));
    setErrors((e) => ({ ...e, [key]: { ...(e[key] ?? {}), [name]: '' } }));
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty.length) return;
    const next: Partial<Record<RuleKey, Record<string, string>>> = {};
    const values: Partial<Record<RuleKey, Record<string, number | boolean>>> = {};
    for (const k of dirty) {
      const r = validateRules(k, drafts[k]);
      if (r.ok) values[k] = r.value;
      else next[k] = r.errors;
    }
    setErrors(next);
    if (Object.keys(next).length) {
      window.setTimeout(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
      toast('Some values need fixing before saving.', 'error');
      return;
    }
    setSaving(true);
    try {
      for (const k of dirty) {
        const v = values[k];
        if (!v) continue;
        const fresh = await putRules(api, k, v);
        qc.setQueryData(opsKeys.settings, (old: unknown) => (old ? { ...(old as Server), [k]: fresh ?? v } : old));
      }
      const now = qc.getQueryData(opsKeys.settings) as unknown as Server | undefined;
      if (now) setDrafts({ catalog: rulesDraft('catalog', now.catalog), catchWeight: rulesDraft('catchWeight', now.catchWeight) });
      toast('Rules saved. They apply from the next product change and the next weighing.');
    } catch (err) {
      toast(errorMessage(err, "Couldn't save the rules. Try again."), 'error');
      void qc.invalidateQueries({ queryKey: opsKeys.settings });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={(e) => void save(e)} noValidate className="stack s16">
      <div className="mk-rules">
        {RULES_SPEC.map((section, si) => {
          const d = drafts[section.key];
          const errs = errors[section.key] ?? {};
          const check = validateRules(section.key, d);
          const preview: Record<string, unknown> = check.ok ? check.value : (server[section.key] ?? {});
          const changed = dirty.includes(section.key);
          return (
            <section key={section.key} className="card dv-card dv-rise" aria-labelledby={`cr-${section.key}`} style={{ ['--i' as string]: si + 1 }}>
              <div className="dv-card-h">
                <h2 id={`cr-${section.key}`} title={section.intro}>
                  {section.title}
                </h2>
                {changed ? <span className="dv-chip">Edited</span> : null}
              </div>
              {section.key === 'catchWeight' ? <ToleranceBand pct={typeof preview.tolerancePct === 'number' ? preview.tolerancePct : null} max={section.fields[0]?.max ?? 30} /> : <RulePreview fields={section.fields} preview={preview} />}
              <ul className="sr-only" aria-live="polite">
                {section.fields.map((f) => (
                  <li key={f.name}>{describeRule(f, preview[f.name])}</li>
                ))}
              </ul>
              <div className="mk-rule-rows">
                {section.fields.map((f) => (
                  <CompactRule key={f.name} id={`cr-${section.key}-${f.name}`} field={f} value={d[f.name] ?? ''} error={errs[f.name] || undefined} onChange={(v) => setField(section.key, f.name, v)} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <div className="card mk-savebar">
        <span className="mk-sub" aria-live="polite">
          {dirty.length ? `Unsaved changes in ${dirty.map((k) => RULES_SPEC.find((s) => s.key === k)?.title).join(', ')}` : 'All changes saved'}
        </span>
        <span className="grow" />
        <button
          type="button"
          className="btn btn-quiet"
          disabled={!dirty.length || saving}
          onClick={() => {
            setDrafts({ catalog: rulesDraft('catalog', server.catalog), catchWeight: rulesDraft('catchWeight', server.catchWeight) });
            setErrors({});
          }}
        >
          Discard changes
        </button>
        <button type="submit" className="btn btn-primary" disabled={!dirty.length || saving} aria-busy={saving || undefined}>
          {saving ? 'Saving…' : 'Save rules'}
        </button>
      </div>
    </form>
  );
}

type Spec = (typeof RULES_SPEC)[number]['fields'][number];

/** The rule values as figures: what a partner can do right now, at a glance. */
function RulePreview({ fields, preview }: { fields: Spec[]; preview: Record<string, unknown> }) {
  return (
    <div className="mk-rule-figs">
      {fields.map((f) => {
        const v = preview[f.name];
        const off = f.kind === 'bool' ? v !== true : typeof v === 'number' && v === 0 && !!f.zeroMeans;
        const text = f.kind === 'bool' ? (v === true ? 'On' : 'Off') : typeof v !== 'number' ? '—' : off ? 'Off' : f.kind === 'peso' ? peso(v) : `${v}%`;
        return (
          <div key={f.name} className={off ? 'off' : ''} title={describeRule(f, v)}>
            <span className="dv-label">{f.short}</span>
            {f.kind === 'bool' ? (
              <span className="mk-onoff">
                <i className={`mk-dot lg ${v === true ? 'ok' : 'off'}`} aria-hidden="true" />
                <Fig text={text} size="md" animate={false} />
              </span>
            ) : (
              <Fig text={text} size="md" />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Catch-weight tolerance as a band: what the customer covers, and where the shop takes over. */
function ToleranceBand({ pct, max }: { pct: number | null; max: number }) {
  const p = pct ?? 0;
  const w = Math.min(100, (p / max) * 100);
  return (
    <div className="mk-band" title={`The customer pays up to ${p}% over the estimate. Above that, the shop absorbs it.`}>
      <div className="mk-band-top">
        <span className="mk-plus" aria-hidden="true">+</span>
        <Fig text={pct === null ? '—' : `${p}%`} size="lg" />
        <span className="mk-sub">over the estimate</span>
      </div>
      <div className="mk-band-bar" aria-hidden="true">
        <span className="est" />
        <span className="cust" style={{ width: `${w}%` }} />
        <span className="shop" />
      </div>
      <div className="mk-band-legend" aria-hidden="true">
        <span>
          <i className="mk-dot lg ok" /> Customer pays
        </span>
        <span>
          <i className="mk-dot lg warn" /> Shop absorbs
        </span>
        <span className="grow" />
        <span className="mk-sub">max {max}%</span>
      </div>
    </div>
  );
}

/** One rule: a short label (the full sentence on hover), a switch or an affixed number box. */
function CompactRule({ id, field: f, value, error, onChange }: { id: string; field: Spec; value: string | boolean; error?: string; onChange(v: string | boolean): void }) {
  const errId = `${id}-err`;
  const tip = [f.label, f.help].filter(Boolean).join('. ');
  if (f.kind === 'bool') {
    return (
      <div className="mk-rule">
        <span className="mk-rule-l" title={tip}>
          <span id={`${id}-l`}>{f.short}</span>

        </span>
        <button type="button" role="switch" id={id} className="switch" aria-checked={value === true} aria-labelledby={`${id}-l`} title={f.help} onClick={() => onChange(value !== true)}>
          <span className="track" aria-hidden="true" />
          <span className="t-label">{value === true ? 'On' : 'Off'}</span>
        </button>
      </div>
    );
  }
  const prefix = f.kind === 'peso' ? '₱' : null;
  const suffix = f.kind === 'percent' ? '%' : null;
  return (
    <div className="mk-rule">
      <label htmlFor={id} className="mk-rule-l" title={tip}>
        {f.short}
      </label>
      <div className="stack s4 mk-rule-in">
        <div className="input-affix" data-invalid={error ? 'true' : undefined} title={f.help}>
          {prefix ? (
            <span aria-hidden="true" className="t-mono">
              {prefix}
            </span>
          ) : null}
          <input id={id} inputMode="decimal" autoComplete="off" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined} />
          {suffix ? (
            <span aria-hidden="true" className="t-mono muted">
              {suffix}
            </span>
          ) : null}
        </div>
        {error ? (
          <span id={errId} className="pill st-error err-pill" style={{ whiteSpace: 'normal' }}>
            {error}
          </span>
        ) : null}
      </div>
    </div>
  );
}
